import { useTranslation } from '@govard/i18n-core/react';
import { i18n } from '../../../i18n/index.js';
import { useMemo, useRef, useState, type ReactElement } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Columns3, LayoutGrid, List } from 'lucide-react';
import { Badge, Button, Space, Tooltip, Typography } from 'antd';
import type { ColumnType } from 'antd/es/table/interface';
import { Table } from 'antd';
import {
  CheckSquareOutlined,
  EditOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

import { PageHeader } from '../../../shared/ui/PageHeader.js';
import {
  ENTITY_NAME_LINK_STYLE,
  LIST_CREATE_BUTTON_STYLE,
  LIST_ICON_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
  SEG_TRACK_STYLE,
  TABLE_SORTER_TOOLTIP,
  listCountStyle,
  segOptionStyle,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
import { FilterBar, FilterSearch } from '../../../shared/ui/FilterBar.js';
import { TagChip } from '../../../components/entity-detail/TagChip.js';
import { SYSTEM_TAGS } from '../../../shared/format/enum-labels.js';
import { ContactListFilters, type ContactListFilter } from '../components/ContactListFilters.js';
import { GridColumnsButton } from '../../../shared/grid/GridColumnsButton.js';
import { useGridProfile, type GridColumnMeta } from '../../../shared/grid/useGridProfile.js';
import { useOrderedColumns } from '../../../shared/grid/useOrderedColumns.js';
import { byDate, byNumber, byText } from '../../../shared/ui/tableSorters.js';
import { formatDateTime } from '../../../shared/format/index.js';
import { useDataViewPagination } from '../../../shared/dataview/useDataViewPagination.js';
import { usePublishSelection } from '../../../shared/selection/useSelectionStore.js';
import { TaskFormModal } from '../../tasks/components/TaskFormModal.js';
import { EntityHistoryButton } from '../../activity/components/EntityHistoryButton.js';
import { DeleteEntityButton } from '../../../shared/ui/DeleteEntityButton.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { ContactFormModal } from '../components/ContactFormModal.js';
import { ContactCardsGrid } from '../components/ContactCardsGrid.js';
import { useContacts } from '../hooks/useContacts.js';
import { useDeleteContactMutation } from '../hooks/useContactMutations.js';
import type { ContactItem } from '../api/contacts.client.js';

const { Text } = Typography;

interface ContactRow {
  id: string;
  fullName: string;
  email?: string | null;
  phone?: string | null;
  dealsCount: number;
  tags: string[];
  createdAt: string;
  companyRef?: { id: string; name: string } | null;
}

type ContactViewMode = 'list' | 'cards';

/** Колонки, которыми управляет вид грида; «Действия» остаются на месте всегда. */
const CONTACT_GRID_COLUMNS: GridColumnMeta[] = [
  {
    key: 'fullName',
    get label() {
      return i18n.t('crm.features.map.features.contacts.pages.contactspage.28');
    },
  },
  {
    key: 'contact',
    get label() {
      return i18n.t('crm.features.map.features.contacts.pages.contactspage.29');
    },
  },
  {
    key: 'company',
    get label() {
      return i18n.t('crm.features.map.features.contacts.pages.contactspage.30');
    },
  },
  {
    key: 'dealsCount',
    get label() {
      return i18n.t('crm.features.map.features.contacts.pages.contactspage.31');
    },
  },
  {
    key: 'tags',
    get label() {
      return i18n.t('crm.features.map.features.contacts.pages.contactspage.32');
    },
  },
  // Дата создания есть во всех гридах и везде скрыта по умолчанию.
  {
    key: 'createdAt',
    get label() {
      return i18n.t('crm.features.map.features.contacts.pages.contactspage.33');
    },
    hiddenByDefault: true,
  },
];

export function ContactsPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [viewMode, setViewMode] = useState<ContactViewMode>('list');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<ContactItem | null>(null);
  const deleteMutation = useDeleteContactMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const contactFilter: ContactListFilter =
    searchParams.get('has') === 'deals'
      ? 'with_deals'
      : searchParams.get('has') === 'email'
        ? 'with_email'
        : 'all';
  const tagFilter = searchParams.get('tag') ?? undefined;
  const setFilter = (name: 'tag' | 'has', value: string | undefined): void => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value === undefined) next.delete(name);
      else next.set(name, value);
      next.delete('page');
      return next;
    });
  };
  // Единая форма выбора строк: список ключей + `onChange`, как в остальных гридах.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Быстрое действие «создать задачу» из строки списка — форма с привязкой к контакту.
  const [taskForContactId, setTaskForContactId] = useState<string | null>(null);
  // Отбор идёт на сервере. Раньше поиск уходил в API, а тег и «со сделками /
  // с email» отсеивались в браузере — и применялись к первой странице выдачи
  // (`take` в сервисе), а не ко всей базе контактов.
  const contactsQuery = useContacts({
    ...(activeQuery ? { q: activeQuery } : {}),
    ...(tagFilter === undefined ? {} : { tag: tagFilter }),
    ...(contactFilter === 'with_deals' ? { has: 'deals' as const } : {}),
    ...(contactFilter === 'with_email' ? { has: 'email' as const } : {}),
  });
  const visibleContacts = useMemo(() => contactsQuery.data ?? [], [contactsQuery.data]);
  const isFiltered = contactFilter !== 'all' || tagFilter !== undefined || activeQuery.length > 0;

  // Публикуем выделение — быстрая заметка цепляется к отмеченным строкам.
  usePublishSelection(
    'contact',
    visibleContacts
      .filter((contact) => selectedIds.includes(contact.id))
      .map((contact) => ({ id: contact.id, label: contact.fullName })),
  );

  const summary = useMemo(
    () => ({
      total: visibleContacts.length,
      withDeals: visibleContacts.filter((c) => c.dealsCount > 0).length,
      withEmail: visibleContacts.filter((c) => Boolean(c.email)).length,
    }),
    [visibleContacts],
  );

  // Словарь тегов накапливаем по всем загруженным выдачам: при выбранном теге
  // сервер отдаёт только контакты с ним, и список тегов схлопнулся бы до одного.
  const knownTags = useRef<Set<string>>(new Set(SYSTEM_TAGS));
  const availableTags = useMemo(() => {
    if (tagFilter !== undefined) knownTags.current.add(tagFilter);
    for (const contact of visibleContacts) {
      for (const tag of contact.tags) {
        knownTags.current.add(tag);
      }
    }
    return Array.from(knownTags.current).sort((a, b) => a.localeCompare(b));
  }, [visibleContacts, tagFilter]);

  const pagination = useDataViewPagination(visibleContacts);

  function handleSearch(value: string): void {
    const nextQuery = value.trim();
    setSearchQuery(nextQuery);
    setActiveQuery(nextQuery);
    pagination.resetPage();
  }

  // Колонки по ключу: порядок и видимость задаёт профиль пользователя.
  const columnMap: Record<string, ColumnType<ContactRow>> = {
    fullName: {
      title: i18n.t('crm.features.features.contacts.pages.contactspage.контакт'),
      dataIndex: 'fullName',
      key: 'fullName',
      // Порядок по умолчанию задаёт сервер (фамилия, затем имя) — здесь только
      // возможность пересортировать, без `defaultSortOrder`.
      sorter: byText<ContactRow>((row) => row.fullName),
      render: (name: string, record) => (
        <Link
          id={`link-contact-${record.id}`}
          to={`/contacts/${record.id}`}
          style={ENTITY_NAME_LINK_STYLE}
        >
          {name}
        </Link>
      ),
    },
    contact: {
      title: `${t('crm.contacts.form.email')} / ${t('crm.contacts.form.phone')}`,
      key: 'contact',
      // Сортируем по тому же значению, которое видно в ячейке: email, иначе телефон.
      sorter: byText<ContactRow>((row) => row.email ?? row.phone),
      render: (_: unknown, record) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {record.email ?? record.phone ?? (
            <Text disabled style={{ fontSize: 13 }}>
              —
            </Text>
          )}
        </Text>
      ),
    },
    company: {
      // Компания видна прямо в списке: частные лица отличаются от сотрудников компаний.
      title: i18n.t('crm.features.features.contacts.pages.contactspage.компания'),
      key: 'company',
      sorter: byText<ContactRow>((row) => row.companyRef?.name),
      render: (_: unknown, record) =>
        record.companyRef ? (
          // stopPropagation — иначе клик перехватывает строка и уводит на контакт.
          <Link
            to={`/companies/${record.companyRef.id}`}
            style={{ color: 'var(--ant-color-primary)' }}
            onClick={(event) => event.stopPropagation()}
          >
            {record.companyRef.name}
          </Link>
        ) : (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {i18n.t('crm.features.features.contacts.pages.contactspage.частное_лицо')}
          </Text>
        ),
    },
    dealsCount: {
      title: i18n.t('crm.features.features.contacts.pages.contactspage.сделки'),
      dataIndex: 'dealsCount',
      key: 'dealsCount',
      width: 100,
      align: 'center',
      sorter: byNumber<ContactRow>((row) => row.dealsCount),
      render: (count: number) => <Badge count={count} showZero style={listCountStyle(count)} />,
    },
    createdAt: {
      title: i18n.t('crm.features.features.contacts.pages.contactspage.дата_создания'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      sorter: byDate<ContactRow>((row) => row.createdAt),
      render: (value: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {formatDateTime(value)}
        </Text>
      ),
    },
    tags: {
      title: i18n.t('crm.features.features.contacts.pages.contactspage.теги'),
      dataIndex: 'tags',
      key: 'tags',
      render: (tags: string[]) => (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag) => (
            <TagChip key={tag} value={tag} />
          ))}
          {tags.length === 0 && (
            <Text disabled style={{ fontSize: 12 }}>
              —
            </Text>
          )}
        </div>
      ),
    },
  };

  // Быстрые действия вместо «Открыть»: строка и так открывает карточку по клику,
  // а здесь — то, что зависит от данных контакта (почта/телефон) или экономит переход.
  const actionsColumn: ColumnType<ContactRow> = {
    title: i18n.t('crm.features.features.contacts.pages.contactspage.действия'),
    key: 'action',
    align: 'center',
    width: 130,
    render: (_: unknown, record) => (
      <Space size={4}>
        <Tooltip
          title={
            record.email
              ? i18n.t('crm.features.dynamic.features.contacts.pages.contactspage.43', {
                  p0: record.email,
                })
              : i18n.t('crm.features.features.contacts.pages.contactspage.нет_email')
          }
        >
          <Button
            size="small"
            type="text"
            aria-label={i18n.t('crm.features.dynamic.features.contacts.pages.contactspage.44', {
              p0: record.fullName,
            })}
            icon={<MailOutlined />}
            disabled={!record.email}
            onClick={(event) => {
              event.stopPropagation();
              window.location.href = `mailto:${record.email}`;
            }}
          />
        </Tooltip>
        <Tooltip
          title={
            record.phone
              ? i18n.t('crm.features.dynamic.features.contacts.pages.contactspage.45', {
                  p0: record.phone,
                })
              : i18n.t('crm.features.features.contacts.pages.contactspage.нет_телефона')
          }
        >
          <Button
            size="small"
            type="text"
            aria-label={i18n.t('crm.features.dynamic.features.contacts.pages.contactspage.46', {
              p0: record.fullName,
            })}
            icon={<PhoneOutlined />}
            disabled={!record.phone}
            onClick={(event) => {
              event.stopPropagation();
              window.location.href = `tel:${record.phone}`;
            }}
          />
        </Tooltip>
        <Tooltip
          title={i18n.t(
            'crm.features.features.contacts.pages.contactspage.создать_задачу_по_контакту',
          )}
        >
          <Button
            size="small"
            type="text"
            aria-label={i18n.t('crm.features.dynamic.features.contacts.pages.contactspage.47', {
              p0: record.fullName,
            })}
            icon={<CheckSquareOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              setTaskForContactId(record.id);
            }}
          />
        </Tooltip>
        <Tooltip title={i18n.t('crm.features.features.contacts.pages.contactspage.редактировать')}>
          <Button
            size="small"
            type="text"
            aria-label={i18n.t('crm.features.dynamic.features.contacts.pages.contactspage.48', {
              p0: record.fullName,
            })}
            icon={<EditOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              const source = (contactsQuery.data ?? []).find((item) => item.id === record.id);
              if (source) setEditingContact(source);
            }}
          />
        </Tooltip>
        <EntityHistoryButton
          entity="contact"
          entityId={record.id}
          title={i18n.t('crm.features.features.contacts.pages.contactspage.история_контакта')}
          subject={record.fullName}
          iconOnly
        />
        {/* Клик по строке открывает карточку — гасим его на кнопке удаления. */}
        <span
          onClick={(event) => event.stopPropagation()}
          role="presentation"
          className="inline-flex"
        >
          <DeleteEntityButton
            iconOnly
            label={i18n.t('crm.features.features.contacts.pages.contactspage.удалить_контакт')}
            title={i18n.t('crm.features.features.contacts.pages.contactspage.удалить_контакт_2')}
            description={i18n.t(
              'crm.features.features.contacts.pages.contactspage.сделки_задачи_и_заметки_контакта_останутся',
            )}
            loading={deleteMutation.isPending && deleteMutation.variables === record.id}
            onConfirm={() => deleteMutation.mutateAsync(record.id)}
          />
        </span>
      </Space>
    ),
  };

  const grid = useGridProfile('contacts', CONTACT_GRID_COLUMNS);
  const ordered = useOrderedColumns<ContactRow>({
    profile: grid,
    columnMap,
    trailing: [actionsColumn],
  });

  return (
    <div className="animate-fade-in flex h-full flex-col">
      <PageHeader
        title={t('crm.contacts.title')}
        subtitle={
          <>
            <span data-testid="contacts-visible-count">{visibleContacts.length}</span>{' '}
            {isFiltered
              ? i18n.t('crm.features.features.contacts.pages.contactspage.контактов_по_фильтру')
              : i18n.t(
                  'crm.features.features.contacts.pages.contactspage.контактов_единая_клиентская_база',
                )}
          </>
        }
        actions={
          <div className="flex max-w-full flex-wrap items-center gap-2">
            <div
              className="flex items-center overflow-hidden p-0.5"
              role="group"
              style={SEG_TRACK_STYLE}
            >
              <Tooltip title={i18n.t('crm.features.features.contacts.pages.contactspage.канбан')}>
                <Button
                  type="text"
                  size="small"
                  aria-label={i18n.t('crm.features.features.contacts.pages.contactspage.канбан_2')}
                  disabled
                  style={segOptionStyle(false, true)}
                >
                  <Columns3 size={14} />
                </Button>
              </Tooltip>
              <Tooltip title={i18n.t('crm.features.features.contacts.pages.contactspage.список')}>
                <Button
                  type="text"
                  size="small"
                  aria-label={i18n.t('crm.features.features.contacts.pages.contactspage.список_2')}
                  aria-pressed={viewMode === 'list'}
                  onClick={() => setViewMode('list')}
                  style={segOptionStyle(viewMode === 'list', false)}
                >
                  <List size={14} />
                </Button>
              </Tooltip>
              <Tooltip title={i18n.t('crm.features.features.contacts.pages.contactspage.карточки')}>
                <Button
                  type="text"
                  size="small"
                  aria-label={i18n.t(
                    'crm.features.features.contacts.pages.contactspage.карточки_2',
                  )}
                  aria-pressed={viewMode === 'cards'}
                  onClick={() => setViewMode('cards')}
                  style={segOptionStyle(viewMode === 'cards', false)}
                >
                  <LayoutGrid size={14} />
                </Button>
              </Tooltip>
            </div>

            <GridColumnsButton profile={grid} />

            <Button
              type="primary"
              aria-label={i18n.t('crm.features.features.contacts.pages.contactspage.новый_контакт')}
              icon={<PlusOutlined />}
              onClick={() => setIsCreateOpen(true)}
              style={LIST_CREATE_BUTTON_STYLE}
            >
              {i18n.t('crm.features.features.contacts.pages.contactspage.новый_контакт_2')}
            </Button>
            <Tooltip title={i18n.t('crm.features.features.contacts.pages.contactspage.обновить')}>
              <Button
                aria-label={i18n.t(
                  'crm.features.features.contacts.pages.contactspage.обновить_контакты',
                )}
                icon={<ReloadOutlined />}
                onClick={() => void contactsQuery.refetch()}
                style={LIST_ICON_BUTTON_STYLE}
              />
            </Tooltip>
          </div>
        }
        testId="contacts-page-header"
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <FilterBar
          search={
            <FilterSearch
              id="contacts-search"
              aria-label={t('crm.contacts.search_label')}
              placeholder={i18n.t(
                'crm.features.features.contacts.pages.contactspage.поиск_по_контакту_или_телефону',
              )}
              value={searchQuery}
              onChange={(value) => {
                setSearchQuery(value);
                setActiveQuery(value.trim());
                pagination.resetPage();
              }}
              onPressEnter={handleSearch}
            />
          }
        >
          <ContactListFilters
            scope={contactFilter}
            tag={tagFilter}
            tags={availableTags}
            onScopeChange={(value) =>
              setFilter(
                'has',
                value === 'with_deals' ? 'deals' : value === 'with_email' ? 'email' : undefined,
              )
            }
            onTagChange={(value) => setFilter('tag', value)}
          />
        </FilterBar>

        {/* Мини-KPI: единая hairline-полоса (spec D `.kpis`), не три карточки. */}
        <div
          className="grid grid-cols-3 overflow-hidden"
          style={{
            background: 'var(--ant-color-border-secondary)',
            border: '1px solid var(--ant-color-border-secondary)',
            borderRadius: 'var(--ant-border-radius-lg, 14px)',
          }}
        >
          {(
            [
              {
                testId: 'contacts-summary-total',
                label: i18n.t('crm.features.features.contacts.pages.contactspage.всего_контактов'),
                value: summary.total,
              },
              {
                testId: 'contacts-summary-deals',
                label: i18n.t('crm.features.features.contacts.pages.contactspage.со_сделками_2'),
                value: summary.withDeals,
              },
              {
                testId: 'contacts-summary-email',
                label: i18n.t('crm.features.features.contacts.pages.contactspage.с_email_2'),
                value: summary.withEmail,
              },
            ] as const
          ).map((cell) => (
            <div
              key={cell.testId}
              className="min-w-0"
              style={{ background: 'var(--ant-color-bg-container)', padding: '12px 16px' }}
            >
              <div
                className="truncate"
                style={{
                  fontSize: 10.5,
                  fontWeight: 550,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  color: 'var(--ant-color-text-tertiary)',
                }}
              >
                {cell.label}
              </div>
              <div
                data-testid={cell.testId}
                style={{
                  fontSize: 22,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  color: 'var(--ant-color-text)',
                  fontVariantNumeric: 'tabular-nums',
                  margin: '4px 0 2px',
                }}
              >
                {cell.value}
              </div>
            </div>
          ))}
        </div>

        {viewMode === 'cards' ? (
          contactsQuery.isError ? (
            <div className="p-6" style={LIST_SURFACE_STYLE}>
              <ErrorState
                title={t('crm.contacts.load_error')}
                error={contactsQuery.error}
                onRetry={() => void contactsQuery.refetch()}
              />
            </div>
          ) : (
            <ContactCardsGrid contacts={visibleContacts} loading={contactsQuery.isLoading} />
          )
        ) : (
          <div
            className="min-h-0 flex-1 crm-checkbox-square"
            data-testid="contacts-list-view"
            style={LIST_SURFACE_STYLE}
          >
            {contactsQuery.isLoading ? (
              <QuietListSkeleton rows={6} />
            ) : contactsQuery.isError ? (
              <div className="p-6">
                <ErrorState
                  title={t('crm.contacts.load_error')}
                  error={contactsQuery.error}
                  onRetry={() => void contactsQuery.refetch()}
                />
              </div>
            ) : (
              <Table<ContactRow>
                showSorterTooltip={TABLE_SORTER_TOOLTIP}
                dataSource={visibleContacts}
                columns={ordered.columns}
                components={ordered.components}
                rowKey="id"
                size="small"
                pagination={pagination.pagination}
                rowSelection={{
                  columnWidth: 48,
                  preserveSelectedRowKeys: true,
                  selectedRowKeys: selectedIds,
                  onChange: (keys) => setSelectedIds(keys.map(String)),
                  getCheckboxProps: (record) => ({
                    title: i18n.t('crm.features.dynamic.features.contacts.pages.contactspage.49', {
                      p0: record.fullName,
                    }),
                    'aria-label': i18n.t(
                      'crm.features.dynamic.features.contacts.pages.contactspage.50',
                      { p0: record.fullName },
                    ),
                  }),
                }}
                locale={{
                  emptyText: (
                    <QuietEmpty
                      text={i18n.t(
                        'crm.features.features.contacts.pages.contactspage.нет_контактов_по_текущим_фильтрам',
                      )}
                    />
                  ),
                }}
                onRow={(record) => ({
                  'data-testid': `contact-row-${record.id}`,
                  style: { cursor: 'pointer' },
                  onClick: () => {
                    navigate(`/contacts/${record.id}`);
                  },
                })}
                style={{ fontSize: 13 }}
              />
            )}
          </div>
        )}
      </div>

      <ContactFormModal open={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
      {editingContact !== null ? (
        <ContactFormModal open contact={editingContact} onClose={() => setEditingContact(null)} />
      ) : null}

      {taskForContactId !== null ? (
        <TaskFormModal
          open
          onClose={() => setTaskForContactId(null)}
          lockedContactId={taskForContactId}
        />
      ) : null}
    </div>
  );
}
